/**
 * NamingManager — Nomenclatura: reglas de nombrado y contadores (estándar de listados).
 *
 * QUÉ HABÍA ANTES
 * ---------------------------------------------------------------------------
 * Media pantalla era un formulario de alta permanente (`card shadow-sm`, inputs
 * de Bootstrap) y la otra media, dos tarjetas con dos tablas de Bootstrap
 * (`table` con `badge bg-*` y `btn-group`) — la del listado de reglas y la de
 * contadores, apiladas. Sin buscador, sin filtros, sin columnas elegibles y sin
 * export; el borrado usaba `window.confirm` y reiniciar un contador,
 * `window.prompt`.
 *
 * AHORA
 * ---------------------------------------------------------------------------
 * Es el listado estándar, con **dos vistas** en el mismo botón de siempre:
 *  · **Reglas** (por defecto): buscador, filtros (tipo de objeto, ámbito, rol,
 *    origen global/empresa, estado y «agrupar por»), columnas elegibles, chips
 *    de KPI que filtran y export. El alta y la edición, que caben en un diálogo,
 *    van al diálogo común.
 *  · **Contadores**: el valor actual de cada serie, con su propio buscador,
 *    filtros, KPIs y export. La acción de cada fila reinicia ese contador (antes
 *    era un `window.prompt`).
 *
 * Para que dos vistas tengan **conjuntos de datos distintos** se usa el
 * `onViewChange` de `ListPage` (y su sincronizado de columnas por vista): es el
 * único listado del CMDB que cambia de dataset, así que la API se amplió para
 * esto en vez de duplicar la pantalla.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TagIcon,
  ListBulletIcon,
  CalculatorIcon,
  ArrowPathIcon,
  PlusIcon,
  ArrowUturnLeftIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import api from '../services/api';
import { OBJECT_TYPES, etiquetaObjeto } from '../config/objectTypes';

const RESET_SCOPE_OPTIONS = [
  { value: 'global', label: 'Global (una sola serie)' },
  { value: 'year', label: 'Por año' },
  { value: 'location', label: 'Por ubicación' },
  { value: 'location-year', label: 'Por ubicación y año' },
  { value: 'rack', label: 'Por rack' },
  { value: 'rack-year', label: 'Por rack y año' },
  { value: 'role', label: 'Por rol' },
  { value: 'role-year', label: 'Por rol y año' },
];

const TOKENS = '{SEQ}, {SEQ:4}, {RAND:4}, {YEAR}, {YY}, {MONTH}, {DAY}, {COMP}, {LOC}, {RACK}, {TYPE}, {ROLE}';

const FORM_VACIO = {
  object_type: 'server',
  pattern: 'SRV-{LOC}-{SEQ:3}',
  reset_scope: 'global',
  role_filter: '',
  priority: 100,
  enabled: true,
  validation_regex: '',
  description: '',
};

const etiquetaTipo = (tipo) => etiquetaObjeto(tipo);
const etiquetaAmbito = (ambito) => RESET_SCOPE_OPTIONS.find((o) => o.value === ambito)?.label || ambito;
const esAmbitoGlobal = (rule) => (rule.reset_scope || 'global') === 'global';
/** Solo las reglas DE LA EMPRESA tienen un contador propio que enseñar. */
const muestraContador = (rule) => esAmbitoGlobal(rule) && Boolean(rule.company_id);

export default function NamingManager({ companyId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');

  const [rules, setRules] = useState([]);
  const [counters, setCounters] = useState([]);
  const [vista, setVista] = useState('rules');

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterScope, setFilterScope] = useState('');
  const [filterOrigin, setFilterOrigin] = useState('');
  const [filterEnabled, setFilterEnabled] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [groupRules, setGroupRules] = useState('');

  const [searchCounters, setSearchCounters] = useState('');
  const [counterType, setCounterType] = useState('');
  const [counterSort, setCounterSort] = useState('');
  const [groupCounters, setGroupCounters] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [resetear, setResetear] = useState(null);
  const [nuevoValor, setNuevoValor] = useState('1');

  /**
   * Carga reglas y contadores. Si la empresa todavía no tiene reglas propias
   * (empresa recién creada o anterior al sembrado), pide al backend que la
   * rellene con los valores por defecto. Es idempotente y no pisa nada.
   */
  const cargar = useCallback(async ({ fillDefaults = false } = {}) => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [rulesResp, countersResp] = await Promise.all([
        api.getNamingRules(companyId),
        api.getNamingCounters(companyId),
      ]);
      let reglas = rulesResp.data || [];
      let contadores = countersResp.data || [];

      const sinReglasPropias = !reglas.some((r) => r.company_id);
      if (fillDefaults || sinReglasPropias) {
        try {
          const seed = await api.ensureNamingDefaults(companyId);
          const resumen = seed?.data || {};
          if (fillDefaults && (resumen.rules_created || resumen.counters_created)) {
            mostrarAviso(
              `${resumen.rules_created || 0} reglas y ${resumen.counters_created || 0} contadores creados`,
            );
          }
          const [rulesResp2, countersResp2] = await Promise.all([
            api.getNamingRules(companyId),
            api.getNamingCounters(companyId),
          ]);
          reglas = rulesResp2.data || reglas;
          contadores = countersResp2.data || contadores;
        } catch (seedErr) {
          // Si el sembrado falla (permisos, red), el listado sigue mostrando
          // lo que haya: no se rompe la pantalla por los valores por defecto.
          if (fillDefaults) {
            setError(seedErr.message || 'No se pudieron aplicar los valores por defecto');
          }
        }
      }

      setRules(reglas);
      setCounters(contadores);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las reglas de nombres');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { setForm(FORM_VACIO); setEditando(null); cargar(); }, [cargar]);

  const mostrarAviso = (texto) => {
    setAviso(texto);
    setTimeout(() => setAviso(''), 2500);
  };

  const guardarRegla = async () => {
    setGuardando(true);
    setError('');
    try {
      const payload = {
        ...form,
        priority: Number(form.priority) || 0,
        role_filter: form.role_filter || null,
        validation_regex: form.validation_regex || null,
        description: form.description || null,
      };
      if (editando) {
        await api.updateNamingRule(editando.id, payload, companyId);
      } else {
        await api.createNamingRule(payload, companyId);
      }
      setFormOpen(false);
      setEditando(null);
      setForm(FORM_VACIO);
      mostrarAviso(editando ? 'Regla actualizada' : 'Regla creada');
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo guardar la regla');
    } finally {
      setGuardando(false);
    }
  };

  const borrarRegla = async (rule) => {
    try {
      await api.deleteNamingRule(rule.id, companyId);
      mostrarAviso('Regla eliminada');
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la regla');
    }
  };

  const reiniciarContador = async () => {
    if (!resetear) return;
    const siguiente = Number(nuevoValor);
    if (Number.isNaN(siguiente) || siguiente < 1) {
      setError('El número siguiente tiene que ser 1 o mayor');
      return;
    }
    setGuardando(true);
    try {
      // El contador guarda el ÚLTIMO usado: si el siguiente debe ser 7, se
      // guarda 6. Así el diálogo habla en el idioma del usuario.
      await api.resetNamingCounter(
        { object_type: resetear.object_type, scope_key: resetear.scope_key, value: siguiente - 1 },
        companyId,
      );
      setResetear(null);
      mostrarAviso(`El siguiente ${etiquetaTipo(resetear.object_type)} será el ${siguiente}`);
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo reiniciar el contador');
    } finally {
      setGuardando(false);
    }
  };

  const abrirEdicion = (rule) => {
    setEditando(rule);
    setForm({
      object_type: rule.object_type || 'server',
      pattern: rule.pattern || '',
      reset_scope: rule.reset_scope || 'global',
      role_filter: rule.role_filter || '',
      priority: rule.priority ?? 100,
      enabled: Boolean(rule.enabled),
      validation_regex: rule.validation_regex || '',
      description: rule.description || '',
    });
    setFormOpen(true);
  };

  /* ── Reglas: filtrado ───────────────────────────────────────────────────── */
  const roles = useMemo(() => {
    const valores = [...new Set(rules.map((r) => r.role_filter).filter(Boolean))];
    return valores.sort().map((v) => ({ value: v, label: v }));
  }, [rules]);

  const filteredRules = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rules
      .filter((r) => {
        if (filterType && r.object_type !== filterType) return false;
        if (filterScope && r.reset_scope !== filterScope) return false;
        if (filterRole && (r.role_filter || '') !== filterRole) return false;
        if (filterOrigin === 'global' && r.company_id) return false;
        if (filterOrigin === 'company' && !r.company_id) return false;
        if (filterEnabled === 'on' && !r.enabled) return false;
        if (filterEnabled === 'off' && r.enabled) return false;
        if (!term) return true;
        return [r.pattern, r.description, etiquetaTipo(r.object_type), r.role_filter]
          .filter(Boolean).join(' ').toLowerCase().includes(term);
      })
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }, [rules, search, filterType, filterScope, filterRole, filterOrigin, filterEnabled]);

  const contarReglas = useMemo(() => ({
    total: rules.length,
    activas: rules.filter((r) => r.enabled).length,
    inactivas: rules.filter((r) => !r.enabled).length,
    globales: rules.filter((r) => !r.company_id).length,
    empresa: rules.filter((r) => Boolean(r.company_id)).length,
  }), [rules]);

  /* ── Contadores: filtrado ───────────────────────────────────────────────── */
  /** Contador global por tipo, para enseñarlo junto a cada regla. */
  const contadoresGlobales = useMemo(() => {
    const mapa = new Map();
    counters.forEach((c) => {
      if ((c.scope_key || 'global') === 'global') mapa.set(c.object_type, c);
    });
    return mapa;
  }, [counters]);

  const abrirContador = (rule) => {
    const actual = contadoresGlobales.get(rule.object_type)?.current_value ?? 0;
    setResetear({
      object_type: rule.object_type,
      scope_key: 'global',
      current_value: actual,
    });
    setNuevoValor(String(actual + 1));
  };

  const tiposConContador = useMemo(() => {
    const valores = [...new Set(counters.map((c) => c.object_type))];
    return valores.sort().map((v) => ({ value: v, label: etiquetaTipo(v) }));
  }, [counters]);

  const filteredCounters = useMemo(() => {
    const term = searchCounters.trim().toLowerCase();
    const lista = counters.filter((c) => {
      if (counterType && c.object_type !== counterType) return false;
      if (!term) return true;
      return [c.scope_key, String(c.current_value ?? ''), etiquetaTipo(c.object_type)]
        .join(' ').toLowerCase().includes(term);
    });
    if (counterSort === 'value') return [...lista].sort((a, b) => (b.current_value || 0) - (a.current_value || 0));
    if (counterSort === 'value-asc') return [...lista].sort((a, b) => (a.current_value || 0) - (b.current_value || 0));
    if (counterSort === 'type') {
      return [...lista].sort((a, b) => etiquetaTipo(a.object_type).localeCompare(etiquetaTipo(b.object_type), 'es'));
    }
    return lista;
  }, [counters, searchCounters, counterType, counterSort]);

  const contarContadores = useMemo(() => ({
    total: counters.length,
    tipos: new Set(counters.map((c) => c.object_type)).size,
    maximo: counters.reduce((max, c) => Math.max(max, c.current_value || 0), 0),
    enCero: counters.filter((c) => (c.current_value || 0) === 0).length,
  }), [counters]);

  /* ── Definiciones por vista ─────────────────────────────────────────────── */
  const esContadores = vista === 'counters';

  const columnasReglas = useMemo(() => [
    {
      key: 'pattern',
      label: 'Patrón',
      sortable: true,
      render: (r) => <span className="list-code">{r.pattern}</span>,
    },
    {
      key: 'object_type',
      label: 'Tipo de objeto',
      sortable: true,
      sortValue: (r) => etiquetaTipo(r.object_type),
      render: (r) => <span className="list-badge">{etiquetaTipo(r.object_type)}</span>,
    },
    {
      key: 'reset_scope',
      label: 'Reinicio del contador',
      sortable: true,
      sortValue: (r) => etiquetaAmbito(r.reset_scope),
      render: (r) => <span className="list-hint">{etiquetaAmbito(r.reset_scope)}</span>,
    },
    {
      key: 'counter',
      label: 'Contador',
      sortable: true,
      sortValue: (r) => (muestraContador(r)
        ? (contadoresGlobales.get(r.object_type)?.current_value ?? 0)
        : -1),
      render: (r) => {
        if (!muestraContador(r)) {
          return <span className="list-hint" title="Las reglas globales no tienen contador propio">—</span>;
        }
        const actual = contadoresGlobales.get(r.object_type)?.current_value ?? 0;
        return (
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
            <strong>{actual}</strong>
            <span className="list-hint">el siguiente será {actual + 1}</span>
          </span>
        );
      },
    },
    {
      key: 'role_filter',
      label: 'Rol',
      sortable: true,
      render: (r) => (r.role_filter
        ? <span className="list-badge">{r.role_filter}</span>
        : <span className="list-hint">Cualquier rol</span>),
    },
    {
      key: 'priority',
      label: 'Prioridad',
      sortable: true,
      render: (r) => <span className="list-code">{r.priority}</span>,
    },
    {
      key: 'origin',
      label: 'Origen',
      sortable: true,
      sortValue: (r) => (r.company_id ? 'Empresa' : 'Global'),
      render: (r) => (r.company_id
        ? <span className="list-badge list-badge--accent">De la empresa</span>
        : <span className="list-badge list-badge--muted" title="La comparten todas las empresas">Global</span>),
    },
    {
      key: 'enabled',
      label: 'Estado',
      sortable: true,
      sortValue: (r) => (r.enabled ? 1 : 0),
      render: (r) => (r.enabled
        ? <span className="list-badge list-badge--on">Activa</span>
        : <span className="list-badge list-badge--off">Desactivada</span>),
    },
    {
      key: 'updated_at',
      label: 'Cambiada',
      sortable: true,
      render: (r) => (r.updated_at
        ? <span className="list-hint">{new Date(r.updated_at).toLocaleDateString()}</span>
        : <span className="list-hint">—</span>),
    },
  ], [contadoresGlobales]);

  const columnasContadores = useMemo(() => [
    {
      key: 'object_type',
      label: 'Tipo de objeto',
      sortable: true,
      sortValue: (c) => etiquetaTipo(c.object_type),
      render: (c) => <span className="list-badge">{etiquetaTipo(c.object_type)}</span>,
    },
    {
      key: 'scope_key',
      label: 'Ámbito de la serie',
      sortable: true,
      render: (c) => <span className="list-code">{c.scope_key || 'global'}</span>,
    },
    {
      key: 'current_value',
      label: 'Valor actual',
      sortable: true,
      render: (c) => (
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
          <strong>{c.current_value ?? 0}</strong>
          <span className="list-hint">el siguiente será {(c.current_value ?? 0) + 1}</span>
        </span>
      ),
    },
    {
      key: 'updated_at',
      label: 'Último cambio',
      sortable: true,
      render: (c) => (c.updated_at
        ? <span className="list-hint">{new Date(c.updated_at).toLocaleString()}</span>
        : <span className="list-hint">—</span>),
    },
  ], []);

  const filtrosReglas = [
    {
      key: 'type', label: 'Tipo de objeto', allLabel: 'Cualquier tipo',
      value: filterType, onChange: setFilterType, options: OBJECT_TYPES,
    },
    {
      key: 'scope', label: 'Reinicio', allLabel: 'Cualquier reinicio',
      value: filterScope, onChange: setFilterScope, options: RESET_SCOPE_OPTIONS,
    },
    {
      key: 'role', label: 'Rol', allLabel: 'Cualquier rol',
      value: filterRole, onChange: setFilterRole, options: roles,
    },
    {
      key: 'origin', label: 'Origen', allLabel: 'Globales y de la empresa',
      value: filterOrigin, onChange: setFilterOrigin,
      options: [
        { value: 'company', label: 'Solo las de esta empresa' },
        { value: 'global', label: 'Solo las globales' },
      ],
    },
    {
      key: 'enabled', label: 'Estado', allLabel: 'Activas y desactivadas',
      value: filterEnabled, onChange: setFilterEnabled,
      options: [
        { value: 'on', label: 'Solo las activas' },
        { value: 'off', label: 'Solo las desactivadas' },
      ],
    },
    {
      key: 'group', label: 'Agrupar por', allLabel: 'Sin agrupar',
      value: groupRules, onChange: setGroupRules,
      options: [
        { value: 'type', label: 'Tipo de objeto' },
        { value: 'scope', label: 'Reinicio del contador' },
        { value: 'enabled', label: 'Estado' },
      ],
    },
  ];

  const filtrosContadores = [
    {
      key: 'type', label: 'Tipo de objeto', allLabel: 'Cualquier tipo',
      value: counterType, onChange: setCounterType, options: tiposConContador,
    },
    {
      key: 'order', label: 'Orden', allLabel: 'Orden natural',
      value: counterSort, onChange: setCounterSort,
      options: [
        { value: 'value', label: 'Los que van más altos primero' },
        { value: 'value-asc', label: 'Los que van más bajos primero' },
        { value: 'type', label: 'Por tipo de objeto' },
      ],
    },
    {
      key: 'group', label: 'Agrupar por', allLabel: 'Sin agrupar',
      value: groupCounters, onChange: setGroupCounters,
      options: [
        { value: 'type', label: 'Tipo de objeto' },
        { value: 'decena', label: 'Rango de valor' },
      ],
    },
  ];

  const groupByReglas = useMemo(() => {
    if (groupRules === 'type') return (r) => etiquetaTipo(r.object_type);
    if (groupRules === 'scope') return (r) => etiquetaAmbito(r.reset_scope);
    if (groupRules === 'enabled') return (r) => (r.enabled ? 'Activas' : 'Desactivadas');
    return undefined;
  }, [groupRules]);

  const groupByContadores = useMemo(() => {
    if (groupCounters === 'type') return (c) => etiquetaTipo(c.object_type);
    if (groupCounters === 'decena') {
      return (c) => {
        const valor = c.current_value || 0;
        const desde = Math.floor(valor / 10) * 10;
        return `${desde} – ${desde + 9}`;
      };
    }
    return undefined;
  }, [groupCounters]);

  const exportReglas = useMemo(() => filteredRules.map((r) => ({
    'Patrón': r.pattern || '',
    'Tipo de objeto': etiquetaTipo(r.object_type),
    'Reinicio del contador': etiquetaAmbito(r.reset_scope),
    'Contador': muestraContador(r)
      ? (contadoresGlobales.get(r.object_type)?.current_value ?? 0)
      : '',
    'Rol': r.role_filter || 'Cualquier rol',
    'Prioridad': r.priority ?? '',
    'Origen': r.company_id ? 'De la empresa' : 'Global',
    'Estado': r.enabled ? 'Activa' : 'Desactivada',
    'Regex de validación': r.validation_regex || '',
    'Descripción': r.description || '',
  })), [filteredRules, contadoresGlobales]);

  const exportContadores = useMemo(() => filteredCounters.map((c) => ({
    'Tipo de objeto': etiquetaTipo(c.object_type),
    'Ámbito de la serie': c.scope_key || 'global',
    'Valor actual': c.current_value ?? 0,
    'Siguiente': (c.current_value ?? 0) + 1,
    'Último cambio': c.updated_at ? new Date(c.updated_at).toLocaleString() : '',
  })), [filteredCounters]);

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1rem 0' }}>
        <div className="list-hint">Selecciona una empresa para gestionar las reglas de nombres.</div>
      </div>
    );
  }

  return (
    <>
      <ListPage
        icon={TagIcon}
        title="Nomenclatura"
        subtitle={esContadores
          ? `${counters.length} contadores · ${filteredCounters.length} filtrados`
          : `${rules.length} reglas · ${filteredRules.length} filtradas`}

        onViewChange={setVista}

        search={esContadores ? searchCounters : search}
        onSearchChange={esContadores ? setSearchCounters : setSearch}
        searchPlaceholder={esContadores
          ? 'Buscar por ámbito o valor…'
          : 'Buscar por patrón o descripción…'}
        filters={esContadores ? filtrosContadores : filtrosReglas}

        exportRows={esContadores ? exportContadores : exportReglas}
        exportFileBase={esContadores ? 'contadores-nomenclatura' : 'reglas-nomenclatura'}
        exportSheetName={esContadores ? 'Contadores' : 'Reglas'}

        columns={esContadores ? columnasContadores : columnasReglas}
        columnStorageKey={esContadores ? 'cmdb_cols_naming_counters' : 'cmdb_cols_naming_rules'}
        defaultVisible={esContadores
          ? ['object_type', 'scope_key', 'current_value', 'updated_at']
          : ['pattern', 'object_type', 'counter', 'reset_scope', 'origin', 'enabled']}
        rows={esContadores ? filteredCounters : filteredRules}
        rowKey={(r) => r.id}
        rowTitle={(r) => (esContadores
          ? `${etiquetaTipo(r.object_type)} · ${r.scope_key}`
          : `Editar la regla ${r.pattern}`)}
        onRowClick={esContadores ? undefined : abrirEdicion}
        groupBy={esContadores ? groupByContadores : groupByReglas}

        kpis={esContadores ? (
          <div className="list-kpis list-kpis--chips">
            <button type="button" className="list-kpi-chip" onClick={() => { setCounterType(''); setSearchCounters(''); }} title="Ver todos los contadores">
              <span className="list-kpi-value">{contarContadores.total}</span>
              <span className="list-kpi-label">contadores</span>
            </button>
            <div className="list-kpi-chip" title="Tipos de objeto con serie propia">
              <span className="list-kpi-value">{contarContadores.tipos}</span>
              <span className="list-kpi-label">tipos</span>
            </div>
            <div className="list-kpi-chip" title="El contador más alto de la empresa">
              <span className="list-kpi-value">{contarContadores.maximo}</span>
              <span className="list-kpi-label">valor más alto</span>
            </div>
            <div className="list-kpi-chip" title="Series que todavía no han dado ningún nombre">
              <span className="list-kpi-value">{contarContadores.enCero}</span>
              <span className="list-kpi-label">sin estrenar</span>
            </div>
          </div>
        ) : (
          <div className="list-kpis list-kpis--chips">
            <button type="button" className="list-kpi-chip" onClick={() => { setFilterEnabled(''); setFilterOrigin(''); setFilterType(''); setFilterScope(''); setFilterRole(''); setSearch(''); }} title="Ver todas las reglas">
              <span className="list-kpi-value">{contarReglas.total}</span>
              <span className="list-kpi-label">reglas</span>
            </button>
            <button type="button" className="list-kpi-chip" onClick={() => setFilterEnabled('on')} title="Ver las que están activas">
              <span className="list-kpi-value">{contarReglas.activas}</span>
              <span className="list-kpi-label">activas</span>
            </button>
            <button type="button" className="list-kpi-chip" onClick={() => setFilterEnabled('off')} title="Ver las desactivadas">
              <span className="list-kpi-value">{contarReglas.inactivas}</span>
              <span className="list-kpi-label">desactivadas</span>
            </button>
            <button type="button" className="list-kpi-chip" onClick={() => setFilterOrigin('company')} title="Ver las de esta empresa">
              <span className="list-kpi-value">{contarReglas.empresa}</span>
              <span className="list-kpi-label">de la empresa</span>
            </button>
            <button type="button" className="list-kpi-chip" onClick={() => setFilterOrigin('global')} title="Ver las globales (las comparten todas las empresas)">
              <span className="list-kpi-value">{contarReglas.globales}</span>
              <span className="list-kpi-label">globales</span>
            </button>
          </div>
        )}

        onCreate={esContadores ? undefined : () => { setEditando(null); setForm(FORM_VACIO); setFormOpen(true); }}
        createLabel="Nueva regla"
        createTitle="Una regla por tipo de objeto, por rol y prioridad"

        headerActions={(
          <>
            {aviso && <span className="list-badge list-badge--on">{aviso}</span>}
            <button
              type="button"
              className="list-btn-secondary"
              onClick={() => cargar({ fillDefaults: true })}
              disabled={loading}
              title="Crea las reglas y contadores que falten con los valores por defecto del CMDB"
            >
              <SparklesIcon style={{ width: 15, height: 15 }} /> Valores por defecto
            </button>
            <button type="button" className="list-btn-secondary" onClick={() => cargar()} disabled={loading}>
              <ArrowPathIcon style={{ width: 15, height: 15 }} /> {loading ? 'Cargando…' : 'Actualizar'}
            </button>
          </>
        )}

        views={[
          { key: 'rules', label: 'Reglas', icon: ListBulletIcon, title: 'Reglas de nombrado (por prioridad)' },
          { key: 'counters', label: 'Contadores', icon: CalculatorIcon, title: 'Valor actual de cada serie de nombres' },
        ]}
        listKey="naming"
        defaultView="rules"

        actions={esContadores ? {
          extra: (c) => (
            <button
              type="button"
              className="list-action-btn"
              onClick={() => { setResetear(c); setNuevoValor(String((c.current_value ?? 0) + 1)); }}
              title="Ajustar este contador (los siguientes nombres seguirán desde el número que pongas)"
            >
              <ArrowUturnLeftIcon />
            </button>
          ),
        } : {
          extra: (r) => (muestraContador(r) ? (
            <button
              type="button"
              className="list-action-btn"
              onClick={() => abrirContador(r)}
              title={`Ajustar el contador de ${etiquetaTipo(r.object_type)} (va por ${contadoresGlobales.get(r.object_type)?.current_value ?? 0})`}
            >
              <CalculatorIcon />
            </button>
          ) : null),
          canEdit: (r) => true,
          onEdit: abrirEdicion,
          canDelete: () => true,
          onDelete: borrarRegla,
          deleteTitle: 'Eliminar regla de nombrado',
          deleteMessage: (r) => (
            <>
              ¿Seguro que quieres eliminar la regla <strong>{r.pattern}</strong> de{' '}
              {etiquetaTipo(r.object_type).toLowerCase()}? Los nombres ya generados no cambian.
            </>
          ),
        }}

        loading={loading && rules.length === 0 && counters.length === 0}
        loadingText="Cargando la nomenclatura…"
        error={error}

        empty={{
          icon: '🏷️',
          title: esContadores
            ? (counters.length === 0 ? 'Todavía no hay contadores' : 'Ningún contador coincide con los filtros')
            : (rules.length === 0 ? 'Todavía no hay reglas de nombres' : 'Ninguna regla coincide con los filtros'),
          subtitle: esContadores
            ? 'Un contador por tipo: el número por el que va la serie y el siguiente que tocará.'
            : 'Una regla dice cómo se llaman los equipos nuevos: patrón, reinicio del contador, rol y prioridad.',
          action: (!esContadores && rules.length === 0) ? (
            <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                type="button"
                className="list-btn-primary"
                onClick={() => cargar({ fillDefaults: true })}
              >
                <SparklesIcon style={{ width: 14, height: 14 }} /> Valores por defecto
              </button>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => { setEditando(null); setForm(FORM_VACIO); setFormOpen(true); }}
              >
                <PlusIcon style={{ width: 14, height: 14 }} /> Nueva regla
              </button>
            </div>
          ) : undefined,
        }}
      />

      {/* ══ Alta y edición de regla ══ */}
      {formOpen && (
        <Dialog
          title={editando ? `Editar la regla ${editando.pattern}` : 'Nueva regla de nombrado'}
          icon={<TagIcon style={{ width: 18, height: 18 }} />}
          maxWidth={640}
          busy={guardando}
          onClose={() => { setFormOpen(false); setEditando(null); }}
          footer={(
            <>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => { setFormOpen(false); setEditando(null); }}
                disabled={guardando}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="list-btn-primary"
                onClick={guardarRegla}
                disabled={guardando || !form.pattern.trim()}
              >
                {guardando ? 'Guardando…' : (editando ? 'Guardar cambios' : 'Crear regla')}
              </button>
            </>
          )}
        >
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label className="list-hint" style={{ flex: '1 1 200px' }}>
              Tipo de objeto
              <select
                className="list-select"
                style={{ width: '100%', marginTop: 4 }}
                value={form.object_type}
                onChange={(e) => setForm({ ...form, object_type: e.target.value })}
              >
                {OBJECT_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="list-hint" style={{ flex: '1 1 220px' }}>
              Reinicio del contador
              <select
                className="list-select"
                style={{ width: '100%', marginTop: 4 }}
                value={form.reset_scope}
                onChange={(e) => setForm({ ...form, reset_scope: e.target.value })}
              >
                {RESET_SCOPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          </div>

          <label className="list-hint" style={{ display: 'block', marginTop: 10 }}>
            Patrón
            <input
              className="list-input"
              style={{ width: '100%', marginTop: 4, fontFamily: 'var(--font-mono)' }}
              value={form.pattern}
              onChange={(e) => setForm({ ...form, pattern: e.target.value })}
              placeholder="SRV-{LOC}-{SEQ:3}"
            />
            <span className="list-hint">Tokens: <span className="list-code">{TOKENS}</span></span>
          </label>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            <label className="list-hint" style={{ flex: '1 1 150px' }}>
              Rol (opcional)
              <input
                className="list-input"
                style={{ width: '100%', marginTop: 4 }}
                value={form.role_filter}
                onChange={(e) => setForm({ ...form, role_filter: e.target.value })}
                placeholder="web, db, cache"
              />
              <span className="list-hint">Solo se aplica si coincide el rol</span>
            </label>
            <label className="list-hint" style={{ flex: '0 1 120px' }}>
              Prioridad
              <input
                className="list-input"
                type="number"
                style={{ width: '100%', marginTop: 4 }}
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              />
              <span className="list-hint">Manda la más alta</span>
            </label>
          </div>

          <label className="list-hint" style={{ display: 'block', marginTop: 10 }}>
            Regex de validación (opcional)
            <input
              className="list-input"
              style={{ width: '100%', marginTop: 4, fontFamily: 'var(--font-mono)' }}
              value={form.validation_regex}
              onChange={(e) => setForm({ ...form, validation_regex: e.target.value })}
              placeholder="^SRV-[A-Z]+-[0-9]{3}$"
            />
          </label>

          <label className="list-hint" style={{ display: 'block', marginTop: 10 }}>
            Descripción (opcional)
            <textarea
              className="list-input"
              rows={2}
              style={{ width: '100%', marginTop: 4, resize: 'vertical' }}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>

          <label className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            Regla activa (si la quitas, deja de aplicarse pero no se pierde)
          </label>
        </Dialog>
      )}

      {/* ══ Ajuste de contador ══ */}
      {resetear && (
        <Dialog
          title={`Contador de ${etiquetaTipo(resetear.object_type)}`}
          icon={<CalculatorIcon style={{ width: 18, height: 18 }} />}
          maxWidth={480}
          busy={guardando}
          onClose={() => setResetear(null)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setResetear(null)} disabled={guardando}>
                Cancelar
              </button>
              <button type="button" className="list-btn-primary" onClick={reiniciarContador} disabled={guardando}>
                {guardando ? 'Guardando…' : 'Fijar el siguiente número'}
              </button>
            </>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            Serie <span className="list-code">{resetear.scope_key || 'global'}</span>. El último número
            usado es el <strong>{resetear.current_value ?? 0}</strong>; ahora mismo tocaría el{' '}
            <strong>{(resetear.current_value ?? 0) + 1}</strong>.
          </p>
          <label className="list-hint" style={{ display: 'block' }}>
            Número siguiente
            <input
              className="list-input"
              type="number"
              min="1"
              style={{ width: '100%', marginTop: 4 }}
              value={nuevoValor}
              onChange={(e) => setNuevoValor(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') reiniciarContador(); }}
            />
          </label>
          <p className="list-hint" style={{ marginBottom: 0 }}>
            Para reiniciar una numeración pon el <strong>1</strong>. Ojo: si lo bajas, los nombres
            nuevos pueden chocar con otros que ya existen.
          </p>
        </Dialog>
      )}
    </>
  );
}
